import { Box, type BoxProps } from '@mui/material';
import React, { createContext, useContext, useMemo } from 'react';
import { type TreeNode, useEditorActiveNode, useEditorStore, useNodeState } from '../Editor';
import { filterNodes } from '../utils/utils';

type Props = {
  children?: React.ReactNode;
  wrapperProps?: Omit<BoxProps, 'children'>;
};

type EditorConfigContextType = {
  node: TreeNode | null;
};

const EditorConfigContext = createContext<EditorConfigContextType | null>(null);

export const EditorActiveConfigPanel = (props: Props) => {
  const { activeNodeRoot } = useEditorActiveNode();

  const boxProps: BoxProps = useMemo(() => {
    const defaultProps = {
      className: `editor-active-config-panel ${activeNodeRoot ? 'active' : ''} ${activeNodeRoot?.config?.alias ?? ''} id--${activeNodeRoot?.id}`,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    };

    return {
      ...defaultProps,
      ...props?.wrapperProps,
    };
  }, [props?.wrapperProps]);

  if (!activeNodeRoot) {
    return <Box {...boxProps}>{props?.children}</Box>;
  }

  return (
    <Box {...boxProps}>
      <EditorConfigContext.Provider
        value={{
          node: activeNodeRoot,
        }}
      >
        <NodeRootConfig />
      </EditorConfigContext.Provider>
    </Box>
  );
};

/**
 * Show the config element
 */
const ConfigElement = ({
  node,
  state,
  isCurrentActiveNode,
}: { node: TreeNode | null | undefined; state: TreeNode['state']; isCurrentActiveNode: boolean }) => {
  if (!node) {
    return null;
  }

  if (!!node?.editorPanelConfig?.usePanelAlias) {
    return <PanelAliasConfigElement />;
  }

  if (typeof node?.editorPanelConfig?.panel === 'function') {
    return node.editorPanelConfig.panel({
      id: node.id,
      state,
      setState: node.setState,
      isCurrentActiveNode,
    });
  }

  if (!!node?.editorPanelConfig?.panel) {
    return node?.editorPanelConfig?.panel;
  }

  // assume if no panel is provided we want to pass the outlet
  // we also do not want the wrapper div since we cannot control the styling
  return <TreeConfigOutlet noWrapperDiv={true} />;
};

const PanelAliasConfigElement = () => {
  const nodes = useEditorStore((store) => store?.nodes);
  const ctx = useContext(EditorConfigContext);

  const panelFromAlias = useMemo(() => {
    if (!ctx?.node) {
      return null;
    }

    const panelAliasToFind = ctx?.node?.editorPanelConfig?.usePanelAlias;

    if (!panelAliasToFind) {
      return null;
    }

    const found = filterNodes((node) => node?.editorPanelConfig?.panelAlias === panelAliasToFind, nodes);

    if (found?.length !== 1) {
      return null;
    }

    return found[0];
  }, [ctx?.node, nodes]);

  if (!panelFromAlias) {
    return null;
  }

  if (typeof panelFromAlias?.editorPanelConfig?.panel === 'function') {
    return panelFromAlias.editorPanelConfig.panel({
      id: panelFromAlias.id,
      state: panelFromAlias.state,
      setState: panelFromAlias.setState,
      isCurrentActiveNode: panelFromAlias.id === ctx?.node?.id,
    });
  }

  if (!!panelFromAlias?.editorPanelConfig?.panel) {
    return panelFromAlias?.editorPanelConfig?.panel;
  }

  return null;
};

type NodeRootConfigProps = {
  forceChildren?: boolean;
};

/**
 * Show the config element for the root node
 */
const NodeRootConfig = (props: NodeRootConfigProps) => {
  const ctx = useContext(EditorConfigContext);
  const state = useNodeState(ctx?.node?.id);
  const { activeNode } = useEditorActiveNode();

  if (!ctx?.node) {
    return null;
  }

  if (props?.forceChildren) {
    return <ConfigElement node={ctx?.node} state={state} isCurrentActiveNode={ctx?.node?.id === activeNode?.id} />;
  }

  if (activeNode?.nodePath?.includes(ctx?.node?.nodePath ?? '')) {
    return <ConfigElement node={ctx?.node} state={state} isCurrentActiveNode={ctx?.node?.id === activeNode?.id} />;
  }

  return <></>;
};

type TreeConfigOutletProps = {
  noWrapperDiv?: boolean;
  forceChildren?: boolean;
  wrapperProps?: BoxProps;
};

export const TreeConfigOutlet = (props: TreeConfigOutletProps) => {
  const ctx = useContext(EditorConfigContext);

  const childrenElements = useMemo(() => {
    if (!ctx?.node) {
      return [];
    }

    const elements: TreeNode[] = [];

    if (ctx?.node.childrenNodes.size > 0) {
      ctx.node.childrenNodes.forEach((child) => {
        elements.push(child);
      });
    }

    return elements;
  }, [ctx?.node?.childrenNodes]);

  if (!ctx?.node) {
    return <></>;
  }

  const boxProps: BoxProps = useMemo(() => {
    const defaultProps = {
      className: `tree-config-outlet ${ctx?.node?.config?.alias ?? ''} id--${ctx?.node?.id}`,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    };

    return {
      ...defaultProps,
      ...props?.wrapperProps,
    };
  }, [props?.wrapperProps]);

  const WrapperComponent = useMemo(() => (props?.noWrapperDiv ? React.Fragment : Box), [props?.noWrapperDiv]);

  return (
    <WrapperComponent {...(props?.noWrapperDiv ? {} : boxProps)}>
      {childrenElements?.map((child, id) => (
        <EditorConfigContext.Provider
          key={`${child.id}_${id}`}
          value={{
            node: child,
          }}
        >
          <NodeRootConfig forceChildren={props?.forceChildren} />
        </EditorConfigContext.Provider>
      ))}
    </WrapperComponent>
  );
};

export default EditorActiveConfigPanel;
